import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useTrip } from "@/hooks/useTrip";
import { useAddParticipant, useParticipants, useRemoveParticipant } from "@/hooks/useParticipants";
import { PageShell } from "@/components/PageShell";
import { TripHeader } from "@/components/TripHeader";
import { useShareTrip } from "@/hooks/useShareTrip";
import { Skeleton } from "@/components/Skeleton";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { TextInput } from "@/components/TextInput";
import { EmptyState } from "@/components/EmptyState";
import { useToast } from "@/components/toast/ToastProvider";
import { participantLabel } from "@/utils/avatar";
import { pluralParticipants } from "@/utils/format";
import "./participants.css";

export function ParticipantsScreen() {
  const { slug = "" } = useParams<{ slug: string }>();
  const tripQ = useTrip(slug);
  const handleShare = useShareTrip(tripQ.data);
  const participantsQ = useParticipants(slug);
  const add = useAddParticipant(slug);
  const remove = useRemoveParticipant(slug);
  const toast = useToast();

  const [name, setName] = useState("");
  const [addError, setAddError] = useState<string>();

  if (tripQ.isLoading || !tripQ.data) {
    return (
      <PageShell>
        <Skeleton variant="block" width="100%" height={120} />
      </PageShell>
    );
  }

  const participants = participantsQ.data?.participants ?? [];

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setAddError(undefined);
    add.mutate(
      { name: name.trim() },
      {
        onSuccess: () => setName(""),
        onError: (err) => {
          if (err instanceof Error && err.message.toLowerCase().includes("conflict")) {
            setAddError("Уже є учасник з таким іменем. Додай уточнення.");
          } else {
            toast.push({ tone: "error", title: "Не вдалось додати" });
          }
        },
      },
    );
  }

  return (
    <PageShell header={<TripHeader trip={tripQ.data} onShare={handleShare} />}>
      <h2 className="wp-pscreen__title">
        {participants.length > 0
          ? `Учасники (${participants.length})`
          : "Учасники"}
      </h2>

      {participants.length === 0 ? (
        <EmptyState
          title="Поки нікого немає"
          description="Додай імена учасників, щоб теги на чеках працювали."
        />
      ) : (
        <ul className="wp-pscreen__list" aria-label={pluralParticipants(participants.length)}>
          {participants.map((p) => (
            <li key={p.participant_id} className="wp-pscreen__row" data-testid="participant-row">
              <Avatar participant={p} size="md" />
              <div className="wp-pscreen__name">{participantLabel(p)}</div>
              <button
                type="button"
                className="wp-pscreen__remove"
                aria-label={`Видалити ${participantLabel(p)}`}
                onClick={() =>
                  remove.mutate(p.participant_id, {
                    onError: () =>
                      toast.push({
                        tone: "warning",
                        title: "Не можна видалити — є витрати",
                      }),
                  })
                }
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="wp-pscreen__add">
        <TextInput
          label="Додати учасника"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="Ім'я"
          error={addError}
          data-testid="add-participant-input"
        />
        <Button type="submit" disabled={!name.trim() || add.isPending} loading={add.isPending}>
          Додати
        </Button>
      </form>
    </PageShell>
  );
}
